// Request/response types for the scaling-analysis endpoints in bpm-lambdas
// metrics/forecasting.ts (bpm-lambdas#8978). They live there, not in bpm-types,
// for now; mirror them here until both move to @blisspointmedia/bpm-types.
import { OutcomeVariableType } from '@blisspointmedia/bpm-types/dist/Forecasting';

export type ScalingGrain = 'finest' | 'channel_platform' | 'channel_tactic' | 'channel';

// ---- POST /startScalingAnalysis --------------------------------------------

export interface StartScalingAnalysisCombo {
  channel: string;
  /** "" when pooled by the grain; Untagged is "N/A". */
  platform: string;
  tactic: string;
  kpi: string;
  grain: ScalingGrain;
  outcomes: OutcomeVariableType[];
  /** $ per week. Omitted = econ's auto ceiling (1.5x the highest week). */
  maxWeeklySpend?: number | null;
}

export interface StartScalingAnalysisRequest {
  client: string;
  /** The Data QA run (a UUID) the card's menus came from; econ reads its raw + eligibility. */
  dataQaId: string;
  requestGroupId?: string;
  combos: StartScalingAnalysisCombo[];
}

/** A combo that didn't start: startNewForecast's failure shape, plus the
 *  combo's position in the request on /startScalingAnalysis failures. */
export interface ComboSubmitFailure {
  channel: string;
  platform: string;
  tactic: string;
  kpi: string;
  message: string;
  /** /startScalingAnalysis only. */
  index?: number;
}

export interface StartScalingAnalysisResponse {
  requestGroupId: string;
  /** In order of the combos that started (failed ones are left out). */
  scalingIds: string[];
  /** Each with its `index`. Nothing started = 200 with `scalingIds: []` and every combo here. */
  failures: (ComboSubmitFailure & { index: number })[];
}

// ---- GET /getScalingAnalyses + /getScalingResults ----------------------------

export const ScalingAnalysisStatus = {
  IN_PROGRESS: 'In Progress',
  COMPLETED: 'Completed',
  FAILED: 'Failed'
} as const;
export type ScalingAnalysisStatus =
  (typeof ScalingAnalysisStatus)[keyof typeof ScalingAnalysisStatus];

/** One requested run; /getScalingAnalyses returns the 100 most recent, newest first.
 *  A run whose status couldn't be read comes back Failed. */
export interface ScalingAnalysis {
  id: string;
  requestGroupId: string;
  dataQaId: string;
  channel: string;
  /** null when the grain pools it. */
  platform: string | null;
  tactic: string | null;
  kpi: string;
  grain: ScalingGrain;
  outcomes: OutcomeVariableType[];
  /** null = auto ceiling. */
  maxWeeklySpend: number | null;
  createdBy: string;
  /** UTC ISO-8601. */
  createdAt: string;
  status: ScalingAnalysisStatus;
  error?: string;
}

export interface GetScalingAnalysesResponse {
  scalingAnalyses: ScalingAnalysis[];
}

/** `result` is econ's `{id}_scaling.json`, passed through as-is (untyped on the wire). */
export interface GetScalingResultsResponse {
  status: ScalingAnalysisStatus;
  result?: unknown;
  error?: string;
}
